import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ChevronDown, type LucideIconData } from 'lucide-angular';
import type { BlogArticleSection, BlogGameRef } from '../../../../shared/models/blog.model';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { BLOG_COPY } from '../../constants/blog.constants';

const GAME_LOGO_MAP: Record<string, string> = {
  'one-piece': '/images/demo/games/logos/one_piece_logo.webp',
  magic: '/images/demo/games/logos/magic_log.webp',
};

@Component({
  selector: 'app-article-toc',
  templateUrl: './article-toc.component.html',
  styleUrl: './article-toc.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
})
export class ArticleTocComponent {
  sections = input.required<BlogArticleSection[]>();
  activeSection = input<string>('');
  game = input.required<BlogGameRef>();
  sectionClick = output<string>();

  protected readonly chevronIcon: LucideIconData = ChevronDown;
  protected readonly copy = BLOG_COPY;
  protected isTocExpanded = signal(false);
  protected copied = signal(false);
  private readonly platformId = inject(PLATFORM_ID);

  protected toggleToc(): void {
    this.isTocExpanded.update(v => !v);
  }

  protected get gameLogo(): string | null {
    const slug = this.game().slug;
    return slug !== 'general' ? (GAME_LOGO_MAP[slug] ?? null) : null;
  }

  protected onSectionClick(sectionId: string): void {
    this.sectionClick.emit(sectionId);
    if (!isPlatformBrowser(this.platformId)) return;
    const el = document.getElementById(sectionId);
    el?.scrollIntoView({ behavior: 'smooth' });
  }

  protected shareTwitter(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(document.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'noopener');
  }

  protected shareFacebook(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'noopener');
  }

  protected shareWhatsApp(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(document.title);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank', 'noopener');
  }

  protected copyUrl(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    navigator.clipboard.writeText(window.location.href).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }
}
